import React, { useState, createContext, useContext } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { BookOpen, List } from 'lucide-react';
import BotBuilder from './BotBuilder';

// Create context for bot builder state
const BotBuilderContext = createContext();

export const useBotBuilder = () => {
  const context = useContext(BotBuilderContext);
  if (!context) {
    throw new Error('useBotBuilder must be used within BotBuilderProvider');
  }
  return context;
};

const BotManagement = () => {
  const location = useLocation();
  const [isBotBuilder, setIsBotBuilder] = useState(false);
  const [botBuilderMode, setBotBuilderMode] = useState(null); // 'create', 'edit', 'builder'
  const [selectedBot, setSelectedBot] = useState(null);

  // Check if we're in bot builder mode (create, edit, or builder routes)
  const showBotBuilder = isBotBuilder || 
                         location.pathname.includes('/builder') || 
                         location.pathname.includes('/edit') || 
                         location.pathname.includes('/create') ||
                         botBuilderMode;
  

  const handleBackToTabs = () => {
    setIsBotBuilder(false);
    setBotBuilderMode(null);
    setSelectedBot(null);
  };


  // Determine the mode based on the current route or state
  const isEditMode = location.pathname.includes('/edit/') || botBuilderMode === 'edit';
  const isCreateMode = location.pathname.includes('/create') || botBuilderMode === 'create';
  const isBuilderMode = location.pathname.includes('/builder/') || botBuilderMode === 'builder';

  const contextValue = {
    setBotBuilderMode: setBotBuilderMode,
    setIsBotBuilder: setIsBotBuilder,
    setSelectedBot: setSelectedBot
  };

  if (showBotBuilder) {
    return (
      <BotBuilder
        bot={selectedBot}
        onBack={handleBackToTabs}
      />
    );
  }

  return (
    <BotBuilderContext.Provider value={contextValue}>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="px-8 pt-8 pb-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Bot Management</h1>
            <p className="text-gray-600 mt-1">Create, manage, and discover automated workflows for your business</p>
          </div>
          
          {/* Navigation Tabs */}
          <nav className="flex space-x-8">
            <Link
              to="/bots/collection"
              className={`flex items-center space-x-2 px-1 py-3 border-b-2 font-medium text-sm transition-colors ${
                location.pathname === '/bots/collection'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BookOpen className="w-5 h-5" />
              <span>Collection</span>
            </Link>
            <Link
              to="/bots/my-list"
              className={`flex items-center space-x-2 px-1 py-3 border-b-2 font-medium text-sm transition-colors ${
                location.pathname === '/bots/my-list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <List className="w-5 h-5" />
              <span>My List</span>
            </Link>
          </nav>
        </div>

        {/* Content Area - Nested routes will be rendered here */}
        <div className="flex-1 px-8 pb-8">
          <Outlet />
        </div>
    </div>
    </BotBuilderContext.Provider>
  );
};

export default BotManagement;
